import { ComboboxItem, ComboboxItemGroup } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { IconAlertCircle, IconFolders } from '@tabler/icons-react';
import type { ProjectModel, ProjectProduct } from '@xver/apis/v1/projects/model';
import { useActiveProject } from '@xver/hooks/useActiveProject';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import { useProjectsQuery } from '@xver/hooks/useProjectsQuery';
import { setActiveProject } from '@xver/store/project/project.actions';
import { cn } from '@xver/utils/classname';
import {
    accessAtLeast,
    productListPathFor,
    projectOptionLabel,
    SELECT_SEARCHABLE_AFTER,
} from '@xver/utils/projects';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

/** Router state a project switch navigates with; ConsoleLayout keeps the drawer open on it. */
export const PROJECT_SWITCH_NAVIGATION_STATE = { keepNavigationOpen: true } as const;

interface ProjectSwitcherProps {
    className?: string;
    /** Marks the combobox `data-autofocus`, so an enclosing Drawer or Modal focuses it on open. */
    autoFocus?: boolean;
    /** Disables, and labels why, each project where the caller can't manage this product. */
    product?: ProjectProduct;
    label?: string;
}

export default function ProjectSwitcher({
    className,
    autoFocus = false,
    product,
    label,
}: ProjectSwitcherProps) {
    const { t } = useTranslation('projects');
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { activeProjectId } = useActiveProject();
    const { data: projects, isPending, isError, refetch, isRefetching } = useProjectsQuery();
    const [announcement, setAnnouncement] = useState('');

    const byId = useMemo(
        () => new Map((projects ?? []).map((project) => [project.id, project])),
        [projects]
    );

    const data = useMemo<ComboboxItemGroup<ComboboxItem>[]>(() => {
        const toItem = (project: ProjectModel) => {
            const name = projectOptionLabel(project, t);
            const blocked = !!product && !accessAtLeast(project.access[product], 'manage');
            return {
                value: project.id,
                label: blocked ? t('deploy.optionNoAccess', { name }) : name,
                disabled: blocked,
            };
        };
        const own = (projects ?? []).filter((project) => project.role === 'owner');
        const shared = (projects ?? []).filter((project) => project.role !== 'owner');
        return [
            { group: t('switcher.ownGroup'), items: own.map(toItem) },
            { group: t('switcher.sharedGroup'), items: shared.map(toItem) },
        ].filter((group) => group.items.length > 0);
    }, [projects, product, t]);

    const badgeFor = (project: ProjectModel | undefined) => {
        if (!project) return null;
        if (project.role !== 'owner') return t(`roles.${project.role}`);
        return project.isDefault ? t('switcher.default') : null;
    };

    const handleChange = (value: string | null) => {
        const next = value ? byId.get(value) : undefined;
        if (!next || next.id === activeProjectId || userId === undefined) return;

        dispatch(setActiveProject({ userId, projectId: next.id }));
        setAnnouncement(t('switcher.switched', { name: next.name }));

        const listPath = productListPathFor(pathname);
        if (listPath) navigate(listPath, { state: PROJECT_SWITCH_NAVIGATION_STATE });
    };

    if (isError && !projects) {
        return (
            <div className={cn('w-full flex flex-col items-start gap-1', className)}>
                <p className="flex items-center gap-1.5 text-sm text-fg/78">
                    <IconAlertCircle size={16} aria-hidden="true" />
                    {t('switcher.loadFailed')}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isRefetching}
                    className="min-h-6 text-sm font-medium underline cursor-pointer disabled:cursor-wait"
                >
                    {t('switcher.retry')}
                </button>
            </div>
        );
    }

    return (
        <div className={cn('w-full flex flex-col items-stretch gap-1', className)}>
            <Select
                label={label ?? t('switcher.label')}
                data={data}
                value={activeProjectId}
                onChange={handleChange}
                allowDeselect={false}
                data-autofocus={autoFocus || undefined}
                disabled={isPending}
                placeholder={isPending ? t('switcher.loading') : undefined}
                searchable={(projects?.length ?? 0) > SELECT_SEARCHABLE_AFTER}
                nothingFoundMessage={t('switcher.nothingFound')}
                leftSection={<IconFolders size={16} aria-hidden="true" />}
                renderOption={({ option }) => {
                    const badge = badgeFor(byId.get(option.value));
                    return (
                        <span className="w-full flex items-center justify-between gap-2">
                            <span className="truncate">{option.label}</span>
                            {badge && (
                                <span className="shrink-0 px-1.5 rounded-sm border-[0.5px] border-line text-xs text-fg/78">
                                    {badge}
                                </span>
                            )}
                        </span>
                    );
                }}
            />
            <Link
                to="/projects"
                className="self-start min-h-6 inline-flex items-center text-sm text-fg/78 underline hover:text-fg"
            >
                {t('switcher.manage')}
            </Link>
            <p role="status" className="sr-only">
                {announcement}
            </p>
        </div>
    );
}

interface ProjectChipProps {
    onClick: () => void;
    expanded: boolean;
    controls: string;
}

/** Compact mobile top-bar button naming the active project; opens the navigation drawer. */
export function ProjectChip({ onClick, expanded, controls }: ProjectChipProps) {
    const { t } = useTranslation('projects');
    const { project } = useActiveProject();

    if (!project) return null;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-haspopup="dialog"
            aria-expanded={expanded}
            aria-controls={controls}
            className="min-w-0 max-w-[40vw] h-9 px-2 inline-flex items-center gap-1.5 rounded-md border-[0.5px] border-line bg-surface-1 text-sm text-fg cursor-pointer hover:bg-fg/5"
        >
            <IconFolders size={16} className="shrink-0" aria-hidden="true" />
            <span className="sr-only">{t('switcher.chipPrefix')} </span>
            <span className="truncate">{projectOptionLabel(project, t)}</span>
        </button>
    );
}
